import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  combineLatest,
  map,
  Observable,
  BehaviorSubject,
  shareReplay,
} from 'rxjs';

import {
  SofQueueMeta,
  SofReceivingStateService,
} from '../../../domain/sof-receiving/sof-receiving-state.service';
import { SofReceivingOrder } from '../../../models/sof-receiving/SofReceivingOrder.interface';
import {
  statusChip,
  StatusChip,
  isOnHold,
  filterOrders,
  orderDate,
  filterByStatus,
  statusCounts,
  StatusKey,
  STATUS_KEYS,
  STATUS_LABELS,
} from '../../../domain/sof-receiving/rules';

/**
 * @component SofQueueComponent
 *
 * The SOF queue — one card per receiving order for the host app's selected
 * location (styled after the prototype's SofCard). Data comes from the shared
 * SofReceivingStateService; the orders arrive already sorted soonest-delivery
 * -first (SofReceivingStateService.loadQueue → sortOrdersSoonestFirst) and are
 * rendered in the given order.
 *
 * Cards show: SOF # (bold) with the bold "Date Required" next to it on the
 * title row, a "Partial PO" pill when any of the SOF's POs has non-empty
 * partialNotes, an ON HOLD badge, the orderTo line, the contact line, and the
 * receiving-status chip. Ordering comes from the state service, not here.
 *
 * The search box filters by SOF number, job number and PO number, and the
 * status chips below it narrow by receiving state. Both are client-side over
 * the already-loaded queue — these are per-facility lists of a modest size,
 * and the PO numbers the search matches on were already fetched for the cards'
 * meta strip, so filtering costs no extra requests.
 */
@Component({
  selector: 'app-sof-queue',
  templateUrl: './sof-queue.component.html',
  styleUrls: ['./sof-queue.component.css'],
})
export class SofQueueComponent {
  /** History mode — only affects the empty-state wording here. */
  @Input() readOnly = false;

  @Output() sofSelected = new EventEmitter<SofReceivingOrder>();

  /** Raw search box text. */
  public readonly search$ = new BehaviorSubject<string>('');

  /** Status chips currently switched on. Every status starts visible. */
  public readonly activeStatuses$ = new BehaviorSubject<ReadonlySet<StatusKey>>(
    new Set(STATUS_KEYS)
  );

  /** Chip definitions, in lifecycle order. */
  public readonly statusKeys = STATUS_KEYS;
  public readonly statusLabels = STATUS_LABELS;

  /**
   * Chip counts, taken from the SEARCH-filtered list rather than the raw queue,
   * so a chip reports how many rows it would actually reveal alongside the
   * current search rather than a number the list cannot produce.
   */
  public readonly statusCounts$: Observable<Record<StatusKey, number>>;

  /**
   * The queue after filtering. Derived rather than stored so it cannot drift
   * from the source list, and so a queue reload re-filters on its own.
   */
  public readonly visibleOrders$: Observable<SofReceivingOrder[]>;

  constructor(public state: SofReceivingStateService) {
    const searched$ = combineLatest([
      this.state.orders,
      this.state.metaBySof,
      this.search$,
    ]).pipe(
      map(([orders, metaBySof, term]) =>
        filterOrders(orders, metaBySof ?? {}, term)
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.statusCounts$ = searched$.pipe(map((orders) => statusCounts(orders)));

    this.visibleOrders$ = combineLatest([searched$, this.activeStatuses$]).pipe(
      map(([orders, active]) => filterByStatus(orders, active))
    );
  }

  public isStatusActive(key: StatusKey): boolean {
    return this.activeStatuses$.value.has(key);
  }

  /**
   * Flips one chip. Turning the LAST chip off restores them all rather than
   * emptying the list — `filterByStatus` reads an empty set as "no filter", and
   * this keeps the button that produced that state showing as on, so the chips
   * never sit in a configuration that claims to show nothing.
   */
  public toggleStatus(key: StatusKey): void {
    const next = new Set(this.activeStatuses$.value);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.activeStatuses$.next(next.size === 0 ? new Set(STATUS_KEYS) : next);
  }

  /** True when at least one chip is off — drives the "clear" affordance. */
  public get statusFiltered(): boolean {
    return this.activeStatuses$.value.size !== STATUS_KEYS.length;
  }

  public resetStatuses(): void {
    this.activeStatuses$.next(new Set(STATUS_KEYS));
  }

  public get searchTerm(): string {
    return this.search$.value;
  }

  public onSearchChange(event: any): void {
    this.search$.next(event?.detail?.value ?? '');
  }

  public clearSearch(): void {
    this.search$.next('');
  }

  /** Clears BOTH filters — what the no-match empty state offers. */
  public clearAllFilters(): void {
    this.search$.next('');
    this.resetStatuses();
  }

  public chipFor(order: SofReceivingOrder): StatusChip {
    return statusChip(order.receivingStatus);
  }

  public onHold(order: SofReceivingOrder): boolean {
    return isOnHold(order.status);
  }

  /**
   * The delivery date as a real Date for the template's date pipe. The raw
   * `dateRequired` is a SQL style-100 string ("Nov 17 2025  7:00AM") that the
   * pipe cannot parse — handing it over directly throws NG02100 and takes the
   * card down with it.
   */
  public dateRequiredFor(order: SofReceivingOrder): Date | null {
    return orderDate(order);
  }

  public metaFor(
    metaBySof: Record<string, SofQueueMeta> | null,
    order: SofReceivingOrder
  ): SofQueueMeta | null {
    return metaBySof?.[order.sof] ?? null;
  }

  public selectSof(order: SofReceivingOrder): void {
    this.sofSelected.emit(order);
  }
}
